/** TODO:
 * - add webgl check https://threejs.org/docs/#manual/en/introduction/WebGL-compatibility-check
 * - add loadingmanager: https://threejs.org/docs/#api/en/loaders/managers/LoadingManager
 * - add react pointer lock: https://github.com/jeongsd/react-pointerlock/blob/master/example/app.js
 * 
 */
import * as THREE from "three";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import SpriteText from 'three-spritetext';

import { coloursToArray } from './utils';
import PointerLockManager from './PointerLockManager';
import ViewFinderManager from './ViewFinderManager';
import Room from './Room';
import Terrain from './Terrain';
import GeoSpawner from './GeoSpawner';
import ImageSpawner from './ImageSpawner';
import SoundController from './SoundController';


import pathToModel from '../../assets/models/stylised_character.glb';

export default class SceneController
{
    constructor()
    {
        this.scene = new THREE.Scene();
  	    this.renderer = new THREE.WebGLRenderer( { antialias: true } );
        this.clock = new THREE.Clock();
        this.raycaster = new THREE.Raycaster();
        this.mouse = new THREE.Vector2();
    }

    setup()
    {
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        document.body.appendChild(this.renderer.domElement);

        this.isDisposed = false;
        this.isSplash = false;
        this.colourArr = (0, coloursToArray)('https://coolors.co/1a535c-4ecdc4-f7fff7-a38800-ff6b6b');
        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 50, 180);

        this.setupCamera();
        this.setupSplashScreen();
    };

    setupCamera()
    {
        let VIEW_ANGLE = 50;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 1;
        let FAR_PLANE = 500;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE );
        this.camera.position.set(-50, 10, 60);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    };

    setupSplashScreen()
    {
        this.isSplash = true;
        this.scene = new THREE.Scene();
        this.scene.background = this.colourArr[0];

        let ambLight = new THREE.AmbientLight(this.colourArr[3]);
        this.scene.add(ambLight);

        let pointLight = new THREE.PointLight(this.colourArr[3], 2, 200, 2);
        // pointLight.castShadow = true;

        let gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, 
        (gltf) => 
        {
            this.root = gltf.scene;
            this.root.add(pointLight);
            pointLight.position.set(1, 1, 1);
            this.root.scale.set(10, 10, 10);
            this.root.position.set(0, 0, 0);
            this.root.rotation.y = -0.7;
            this.root.traverse(child => {
                if(child.isMesh) 
                {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    child.material.metalness = 0;
                }    
            });
            this.scene.add(this.root);

        });
        console.log('Loading Complete!');

        let intro1 = "Welcome to my game \n with no rules and no winners.";
        let intro2 = "Press 'C' to enter \n W A S P + mouse to look around";
        // let intro3 = "WASP + mouse to look around";
        let text1 = new SpriteText(intro1, 1.5);
        let text2 = new SpriteText(intro2, 2);
        // let text3 = new SpriteText(intro3, 2);
        text1.position.set(-2, -3, 5);
        text1.fontFace = 'Sulphur Point';
        text2.position.set(-2, -10, 5);
        text2.fontFace = 'Sulphur Point';
        // text3.position.set(-2, -15, 5);
        // text3.fontFace = 'Sulphur Point';
        this.scene.add(text1);
        this.scene.add(text2);
        // this.scene.add(text3);
    };

    setupSound(mesh)
    {
        this.soundController = new SoundController(this.camera);
        this.soundController.addPositionalSound(mesh);
        // soundController.playSound();     
    }

    setupMainScene()
    {
        this.isSplash = false;
        this.disposed = false;
        this.root.children.forEach(child => 
        {
            if(child.isMesh)
            {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene = new THREE.Scene();
        this.scene.background = this.colourArr[0];
        this.viewFinder = new ViewFinderManager();

        let hemiLight = new THREE.HemisphereLight(this.colourArr[4], this.colourArr[0], 1);
		hemiLight.position.set(0, 50, 0);
        this.scene.add(hemiLight);

        this.scene.fog = new THREE.Fog(this.colourArr[0], 50, 180);
        // console.log(this.renderer.getMaxAnisotropy());

        this.setupControls();
        this.setupSubjects();
    };

    setupControls()
    {
        this.controls = new PointerLockManager(this.scene, this.camera);
    };

    setupSubjects()
    {
        this.sceneSubjects = [];
        let terrain = new Terrain(this.scene, this.colourArr[3]); //this.colourArr[2]
        terrain.setup();
        this.room = new Room(this.scene, this.colourArr[3]);
        let size = this.room.getWidth();
        this.sceneSubjects.push(new GeoSpawner(this.scene, size));

        this.images = new ImageSpawner(this.scene, size);
        let gltfLoader = new GLTFLoader();
        gltfLoader.load(pathToModel, 
        (gltf) => 
        {
            gltf.scene.scale.set(20, 20, 20);
            gltf.scene.position.set(0, 0, 0);
            gltf.scene.traverse(child => {
                if(child.isMesh) 
                {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    // child.material.metalness = 0;
                }    
            });
            this.scene.add(gltf.scene);
            this.setupSound(gltf.scene);
        });
        console.log('Loading Complete!');
    };

    updateSceneSubjects(time)
    {
        for(let i=0; i<this.sceneSubjects.length; i++)
        {
            this.sceneSubjects[i].update(time);
        }
    };

    update() 
    {
        if(this.isDisposed) return;
        let delta = this.clock.getElapsedTime();
        if (isNaN(delta) || delta > 1000 || delta === 0 ) 
        {
	        delta = 1000/60;
        }
        if(this.root)
        {
            this.root.rotation.y += 0.001;
            
        }
        if(!this.isSplash)
        {
            this.controls.update();
            this.updateSceneSubjects(delta);
            this.images.update();
        }    
        this.renderer.render(this.scene, this.camera);
    };

    onWindowResize()
    {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize( window.innerWidth, window.innerHeight);
    };

    onMouseDown(e)
    {
        if(this.isDisposed) return;
        if(!this.isSplash)
        {
            this.viewFinder.onActionStart();
            for(let i=0; i<this.sceneSubjects.length; i++)
            {
                this.sceneSubjects[i].onMouseDown(e);
            };
            if(this.images) this.images.onMouseDown(e);
        }
    };

    onMouseUp(e)
    {
        if(this.isDisposed) return;
        if(!this.isSplash)
        {
            this.viewFinder.onActionEnd();
            if(this.images) this.images.onMouseUp(e);
        }
    };

    onMouseMove(e)
    {
        this.mouse.x = ( e.clientX / this.renderer.domElement.clientWidth ) * 2 - 1;
        this.mouse.y = - ( e.clientY / this.renderer.domElement.clientHeight ) * 2 + 1;
        if(this.images) this.images.onMouseMove(e);
    };

    onKeyDown(e)
    {
        if(this.controls) this.controls.onKeyDown(e);
    };

    onKeyUp(e)
    {
        if(this.controls) this.controls.onKeyUp(e);
    };

    dispose()
    {
        this.isDisposed = true;
        this.scene.children.forEach(child => {
            if(child.isMesh)
            {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
        if(this.controls) this.controls.dispose();
        if(this.soundController) this.soundController.dispose();
        if(this.renderer.domElement) document.body.removeChild(this.renderer.domElement);
    };
};



